import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { ArrowsUpDown, Check, ChevronDown, Close, Search } from "../icons/icons";
import { cx } from "../lib/cx";
import { useUiStrings } from "../strings/ui-strings";

export interface ListChipOption {
	value: string;
	label: string;
	/** Printed small after the label ("Air 4"). Omit when the API gives no count. */
	count?: number;
	icon?: ReactNode;
}

/** A row of option chips — the prototype's `chipB`. Single-select by default: clicking the pressed chip clears it. */
export interface ListChipOptionsGroup {
	kind: "options";
	key: string;
	/** aria-label for this group of chips. */
	label?: string;
	options: ReadonlyArray<ListChipOption>;
	value: ReadonlyArray<string>;
	multiple?: boolean;
	onChange: (value: string[]) => void;
}

/** One chip that opens a list ("Country ▾") — the prototype's dropdown chips on View Records. */
export interface ListChipMenu {
	kind: "menu";
	key: string;
	/** The chip's caption while nothing is picked, and the menu's aria-label. */
	label: string;
	options: ReadonlyArray<ListChipOption>;
	value: string | null;
	onChange: (value: string | null) => void;
	/** Search box placeholder in the menu (shown from 8 options up). */
	searchPlaceholder?: string;
	disabled?: boolean;
}

/**
 * One chip that opens a list where several can be ticked ("Status ▾"). Each
 * tick applies straight away (the prototype's picker). The caption shows the
 * one pick, or the chip's label with the number picked.
 */
export interface ListChipMultiMenu {
	kind: "multi-menu";
	key: string;
	label: string;
	options: ReadonlyArray<ListChipOption>;
	value: ReadonlyArray<string>;
	onChange: (value: string[]) => void;
	searchPlaceholder?: string;
	disabled?: boolean;
}

/** An on/off chip ("Urgent printing"). */
export interface ListChipToggle {
	kind: "toggle";
	key: string;
	label: string;
	pressed: boolean;
	onChange: (pressed: boolean) => void;
	count?: number;
	icon?: ReactNode;
}

export type ListChip = ListChipOptionsGroup | ListChipMenu | ListChipMultiMenu | ListChipToggle;

export interface ListSortOption {
	value: string;
	label: string;
}

export interface ListToolbarProps {
	/**
	 * The APPLIED search (the URL's). The box drafts locally and calls
	 * `onSearch` on Enter, the search button, or ×. Omit `onSearch` when the
	 * endpoint has no search: the box is left out, chips stay.
	 */
	search?: string;
	onSearch?: (value: string) => void;
	searchPlaceholder?: string;
	/** aria-label of the input. Defaults to the placeholder. */
	searchLabel?: string;
	sort?: {
		value: string;
		options: ReadonlyArray<ListSortOption>;
		onChange: (value: string) => void;
	};
	chips?: ReadonlyArray<ListChip>;
	/** Drawn right after the search box (the prototype's "Filters (n)" button). */
	afterSearch?: ReactNode;
	/** Drawn after the sort select (a view toggle). */
	end?: ReactNode;
	/**
	 * Empties the search and every chip — the page writes one URL patch. The
	 * "Clear all" chip shows while `active` (default: search or any chip set).
	 */
	onClearAll?: () => void;
	active?: boolean;
	className?: string;
}

/**
 * The list page's search + sort + quick-filter chips (prototype `.rtools`):
 * a full-width search with the hint inside and a clear ×, a sort select on
 * the right, chips with counts underneath and "Clear all" when anything is on.
 *
 * Controlled: values in, changes out — the page keeps them in the URL. The
 * search never fires per keystroke (platform rule): Enter or the button
 * applies it. Chips and sort apply on click, like the prototype.
 */
export function ListToolbar({
	search = "",
	onSearch,
	searchPlaceholder,
	searchLabel,
	sort,
	chips,
	afterSearch,
	end,
	onClearAll,
	active,
	className,
}: ListToolbarProps) {
	const { list: strings } = useUiStrings();
	const [draft, setDraft] = useState(search);
	useEffect(() => setDraft(search), [search]);
	const sortId = useId();

	const anyChip = (chips ?? []).some((c) =>
		c.kind === "options" || c.kind === "multi-menu" ? c.value.length > 0 : c.kind === "menu" ? c.value !== null : c.pressed,
	);
	const showClear = !!onClearAll && (active ?? (search !== "" || anyChip));
	const placeholder = searchPlaceholder ?? strings.searchPlaceholder;

	const apply = () => {
		const next = draft.trim();
		if (next !== search) onSearch?.(next);
	};

	return (
		<div className={cx("grid gap-[0.75rem]", className)}>
			{(onSearch || sort || afterSearch || end) && (
				<div className="flex flex-wrap gap-[0.5rem] min-[45rem]:flex-nowrap">
					{onSearch && (
						<form
							role="search"
							className="flex h-[2.875rem] min-w-0 flex-[1_1_100%] items-center gap-[0.5rem] rounded-[0.5rem] border border-gms-line-2 bg-base-100 pr-[0.375rem] pl-[0.75rem] transition-[border-color,box-shadow] focus-within:border-gms-navy focus-within:shadow-[0_0_0_3px_rgba(46,49,146,0.12)] min-[45rem]:flex-[1_1_auto]"
							onSubmit={(e) => {
								e.preventDefault();
								apply();
							}}
						>
							<Search className="size-[1.125rem] shrink-0 text-gms-ink-3" aria-hidden />
							<input
								type="search"
								value={draft}
								onChange={(e) => setDraft(e.target.value)}
								placeholder={placeholder}
								aria-label={searchLabel ?? placeholder}
								autoComplete="off"
								maxLength={100}
								className="h-full min-w-0 flex-1 bg-transparent text-[0.9375rem] text-gms-navy-ink outline-none placeholder:text-gms-ink-3/80 [&::-webkit-search-cancel-button]:hidden"
							/>
							{draft !== "" && (
								<button
									type="button"
									aria-label={strings.clearSearch}
									title={strings.clearSearch}
									onClick={() => {
										setDraft("");
										if (search !== "") onSearch?.("");
									}}
									className="grid size-[2rem] shrink-0 cursor-pointer place-items-center rounded-full text-gms-ink-3 hover:bg-gms-soft"
								>
									<Close className="size-[1rem]" />
								</button>
							)}
							<button
								type="submit"
								aria-label={strings.search}
								title={strings.search}
								className="grid h-[2.125rem] shrink-0 cursor-pointer place-items-center rounded-[0.375rem] px-[0.625rem] text-gms-navy hover:bg-gms-navy-soft focus-visible:outline-2 focus-visible:outline-gms-navy"
							>
								<Search className="size-[1rem]" />
							</button>
						</form>
					)}

					{afterSearch}

					{sort && (
						// Her `.rt__sort`: on phones it takes what is left beside Filters / the view switch (one row), from 720px a 200px column.
						<label htmlFor={sortId} className="relative flex min-w-0 flex-[1_1_0] items-center min-[45rem]:flex-[0_0_12.5rem]">
							<ArrowsUpDown aria-hidden className="pointer-events-none absolute left-[0.75rem] size-[1rem] text-gms-ink-3" />
							<select
								id={sortId}
								aria-label={strings.sort}
								value={sort.value}
								onChange={(e) => sort.onChange(e.target.value)}
								className="h-[2.875rem] w-full min-w-0 cursor-pointer truncate appearance-none rounded-[0.5rem] border border-gms-line-2 bg-base-100 pr-[2.25rem] pl-[2.25rem] text-[0.875rem] text-gms-navy-ink outline-none focus-visible:border-gms-navy focus-visible:shadow-[0_0_0_3px_rgba(46,49,146,0.12)]"
							>
								{sort.options.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
							<ChevronDown aria-hidden className="pointer-events-none absolute right-[0.75rem] size-[1rem] text-gms-ink-3" />
						</label>
					)}

					{end}
				</div>
			)}

			{((chips && chips.length > 0) || showClear) && (
				// Her `.fchips`: on phones ONE row that scrolls sideways under the page gutter
				// (no stack of wrapped rows); from 720px up the chips wrap.
				<div
					role="group"
					aria-label={strings.quickFilters}
					className="-mx-[1rem] flex items-center gap-[0.375rem] overflow-x-auto px-[1rem] pb-[0.125rem] [scrollbar-width:none] min-[45rem]:mx-0 min-[45rem]:flex-wrap min-[45rem]:overflow-visible min-[45rem]:px-0 min-[45rem]:pb-0 [&::-webkit-scrollbar]:hidden"
				>
					{chips?.map((c) =>
						c.kind === "options" ? (
							<OptionChips key={c.key} group={c} />
						) : c.kind === "menu" ? (
							<MenuChip
								key={c.key}
								chip={c}
								selected={c.value === null ? [] : [c.value]}
								onToggle={(v) => c.onChange(v === c.value ? null : v)}
								onClear={() => c.onChange(null)}
							/>
						) : c.kind === "multi-menu" ? (
							<MenuChip
								key={c.key}
								chip={c}
								multiple
								selected={c.value}
								onToggle={(v) => c.onChange(c.value.includes(v) ? c.value.filter((x) => x !== v) : [...c.value, v])}
								onClear={() => c.onChange([])}
							/>
						) : (
							<Chip key={c.key} pressed={c.pressed} onClick={() => c.onChange(!c.pressed)} count={c.count} icon={c.icon}>
								{c.label}
							</Chip>
						),
					)}
					{showClear && (
						<button
							type="button"
							onClick={onClearAll}
							className="inline-flex h-[2.125rem] shrink-0 cursor-pointer items-center gap-[0.375rem] rounded-full px-[0.75rem] text-[0.78125rem] font-medium whitespace-nowrap text-gms-ink-2 hover:text-gms-navy"
						>
							<Close className="size-[0.875rem]" />
							{strings.clearAll}
						</button>
					)}
				</div>
			)}
		</div>
	);
}

/* ------------------------------------------------------------------ */

const chipBase =
	"inline-flex h-[2.125rem] max-w-full shrink-0 cursor-pointer items-center gap-[0.375rem] rounded-full border px-[0.75rem] text-[0.78125rem] whitespace-nowrap outline-none transition-colors focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:size-[1rem] [&>svg]:shrink-0";
const chipOff = "border-gms-line bg-base-100 font-medium text-gms-ink-2 hover:enabled:border-gms-navy";
const chipOn = "border-transparent bg-gms-navy-soft font-semibold text-gms-navy";

function Count({ n, pressed }: { n: number; pressed: boolean }) {
	const { formatNumber } = useUiStrings();
	return (
		<span className={cx("text-[0.71875rem] font-bold tabular-nums", pressed ? "text-gms-navy" : "text-gms-ink-3")}>{formatNumber(n)}</span>
	);
}

function Chip({
	pressed,
	onClick,
	count,
	icon,
	children,
}: {
	pressed: boolean;
	onClick: () => void;
	count?: number;
	icon?: ReactNode;
	children: ReactNode;
}) {
	return (
		<button type="button" aria-pressed={pressed} onClick={onClick} className={cx(chipBase, pressed ? chipOn : chipOff)}>
			{icon}
			<span className="truncate">{children}</span>
			{count !== undefined && <Count n={count} pressed={pressed} />}
		</button>
	);
}

function OptionChips({ group }: { group: ListChipOptionsGroup }) {
	const toggle = (v: string) => {
		const on = group.value.includes(v);
		if (group.multiple) group.onChange(on ? group.value.filter((x) => x !== v) : [...group.value, v]);
		else group.onChange(on ? [] : [v]);
	};
	return (
		// `contents`: the chips flow (and wrap) in the toolbar's own row, the group stays labelled.
		<div role="group" aria-label={group.label} className="contents">
			{group.options.map((o) => (
				<Chip key={o.value} pressed={group.value.includes(o.value)} onClick={() => toggle(o.value)} count={o.count} icon={o.icon}>
					{o.label}
				</Chip>
			))}
		</div>
	);
}

function MenuChip({
	chip,
	selected,
	onToggle,
	onClear,
	multiple = false,
}: {
	chip: ListChipMenu | ListChipMultiMenu;
	selected: ReadonlyArray<string>;
	onToggle: (value: string) => void;
	onClear: () => void;
	multiple?: boolean;
}) {
	const { select: selectStrings } = useUiStrings();
	const [open, setOpen] = useState(false);
	const [q, setQ] = useState("");
	const root = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const menu = useRef<HTMLDivElement>(null);
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
	const listId = useId();
	// The menu is `position: fixed` — the chip row may scroll sideways and would clip an
	// absolute one — so it is placed from the chip's rect, and re-placed on any scroll or
	// resize (a phone keyboard opening for the menu's search resizes the viewport).
	const place = useCallback(() => {
		const el = trigger.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		const width = Math.min(240, window.innerWidth - 32);
		setPos({ top: r.bottom + 4, left: Math.max(16, Math.min(r.left, window.innerWidth - width - 16)) });
	}, []);
	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => {
			if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
		};
		const onMove = (e: Event) => {
			if (menu.current && e.target instanceof Node && menu.current.contains(e.target)) return;
			place();
		};
		document.addEventListener("mousedown", onDown);
		window.addEventListener("scroll", onMove, true);
		window.addEventListener("resize", onMove);
		return () => {
			document.removeEventListener("mousedown", onDown);
			window.removeEventListener("scroll", onMove, true);
			window.removeEventListener("resize", onMove);
		};
	}, [open, place]);
	const toggleOpen = () => {
		if (!open) place();
		setOpen((o) => !o);
	};
	useEffect(() => {
		if (!open) setQ("");
	}, [open]);

	const pickedAll = chip.options.filter((o) => selected.includes(o.value));
	const picked = pickedAll.length === 1 ? pickedAll[0] : undefined;
	const searchable = chip.options.length >= 8;
	const needle = q.trim().toLocaleLowerCase();
	const shown = needle ? chip.options.filter((o) => o.label.toLocaleLowerCase().includes(needle)) : chip.options;

	// Single: a pick closes the menu. Multiple: it stays open for the next tick.
	const pick = (v: string) => {
		onToggle(v);
		if (!multiple) setOpen(false);
	};
	const clear = () => {
		onClear();
		setOpen(false);
	};
	const onKey = (e: KeyboardEvent) => {
		if (e.key === "Escape") {
			e.stopPropagation();
			setOpen(false);
		}
	};

	return (
		<div ref={root} className="relative" onKeyDown={onKey}>
			<button
				ref={trigger}
				type="button"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={open ? listId : undefined}
				aria-pressed={pickedAll.length > 0}
				disabled={chip.disabled}
				onClick={toggleOpen}
				className={cx(chipBase, pickedAll.length > 0 ? chipOn : chipOff)}
			>
				<span className="truncate">{picked ? picked.label : chip.label}</span>
				{pickedAll.length > 1 && <Count n={pickedAll.length} pressed />}
				<ChevronDown className={cx("transition-transform", open && "rotate-180")} />
			</button>
			{open && (
				<div
					ref={menu}
					style={pos ?? undefined}
					className="fixed z-30 flex w-[15rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[0.625rem] border border-gms-line bg-base-100 shadow-[0_8px_24px_rgba(18,20,63,0.12)]">
					{searchable && (
						<label className="flex items-center gap-[0.5rem] border-b border-gms-line px-[0.625rem]">
							<Search className="size-[0.875rem] shrink-0 text-gms-ink-3" />
							<input
								// biome-ignore lint/a11y/noAutofocus: the menu opened on purpose; typing filters it
								autoFocus
								value={q}
								onChange={(e) => setQ(e.target.value)}
								placeholder={chip.searchPlaceholder ?? selectStrings.searchPlaceholder}
								aria-label={chip.searchPlaceholder ?? selectStrings.searchPlaceholder}
								className="min-w-0 flex-1 bg-transparent py-[0.5rem] text-[0.8125rem] outline-none"
							/>
						</label>
					)}
					<ul id={listId} role="listbox" aria-multiselectable={multiple || undefined} aria-label={chip.label} className="max-h-[16rem] overflow-y-auto p-[0.25rem]">
						{shown.length === 0 && <li className="px-[0.625rem] py-[0.75rem] text-center text-[0.8125rem] text-gms-ink-3">{selectStrings.noMatches}</li>}
						{shown.map((o) => {
							const sel = selected.includes(o.value);
							return (
								<li key={o.value} role="option" aria-selected={sel}>
									<button
										type="button"
										onClick={() => pick(o.value)}
										className={cx(
											"flex w-full cursor-pointer items-center gap-[0.5rem] rounded-[0.375rem] px-[0.625rem] py-[0.4375rem] text-left text-[0.8125rem] hover:bg-gms-soft",
											sel ? "font-semibold text-gms-navy" : "text-gms-navy-ink",
										)}
									>
										{o.icon}
										<span className="min-w-0 flex-1 truncate">{o.label}</span>
										{o.count !== undefined && <Count n={o.count} pressed={sel} />}
										{sel && <Check className="size-[0.875rem] shrink-0" />}
									</button>
								</li>
							);
						})}
					</ul>
					{pickedAll.length > 0 && (
						<button
							type="button"
							onClick={clear}
							className="flex cursor-pointer items-center gap-[0.375rem] border-t border-gms-line px-[0.875rem] py-[0.5rem] text-left text-[0.78125rem] font-medium text-gms-ink-2 hover:bg-gms-soft"
						>
							<Close className="size-[0.875rem]" />
							{selectStrings.clear}
						</button>
					)}
				</div>
			)}
		</div>
	);
}
